import { SxProps, Theme } from '@mui/material';

/**
 * The four sizes a person is drawn at, in the places they are drawn.
 *
 * Named rather than numeric at the call sites, so a list and a table showing the same people
 * cannot disagree by two pixels -- the same argument `RecordCard` makes about the six lists
 * it is shared by. `xs` is a chip's avatar, `sm` a row in a list, `md` a page heading, `lg`
 * the account card on the profile page.
 *
 * `md` and `lg` match the two avatars that existed before this component did: 36px in the app
 * bar, 88px on the profile page. Those two numbers are the reason `MAX_AVATAR_EDGE` is 512.
 */
export const AVATAR_SIZES = { xs: 24, sm: 32, md: 36, lg: 88 } as const;

export type AvatarSize = keyof typeof AVATAR_SIZES;

/** Font size for the fallback initial, roughly 45% of the circle at every size. */
const FONT_SIZES: Record<AvatarSize, string> = {
    xs: '0.7rem',
    sm: '0.85rem',
    md: '0.875rem',
    lg: '2rem',
};

/**
 * One recipe for every avatar in the app, built once per size rather than per render.
 *
 * **A constant rather than a `(theme, size) => ({...})` factory**, which is the shape most
 * styles in this codebase take, and the exception is deliberate: a paged patient list draws
 * twenty-five of these and re-renders on every keystroke of the search box, so a factory
 * would hand emotion a brand-new object to serialise twenty-five times a keystroke. The
 * colours are written as theme paths -- which `sx` resolves itself -- so nothing here needs a
 * `useTheme` and the object can be made once at module load.
 *
 * The background and weight only ever show through when there is no picture: MUI's `Avatar`
 * paints its background behind the children it falls back to, and covers both with the image
 * when `src` loads. So this is simultaneously the styling of a photograph and the styling of
 * a letter, and it has to look deliberate as both.
 *
 * `secondary` rather than `primary`, which is what the header's avatar already used and what
 * keeps a row of initials in a patient list from competing with the clinic's primary colour
 * on every button beside it.
 */
const circle = (size: AvatarSize): SxProps<Theme> => ({
    width: AVATAR_SIZES[size],
    height: AVATAR_SIZES[size],
    fontSize: FONT_SIZES[size],
    fontWeight: 700,
    backgroundColor: 'secondary.main',
    color: 'secondary.contrastText',
    // A photograph is not always the shape of its box after a browser rounds the two
    // dimensions differently; cover crops rather than letterboxes, which a circle needs.
    '& .MuiAvatar-img': { objectFit: 'cover' },
});

export const USER_AVATAR_STYLES: Record<AvatarSize, SxProps<Theme>> = {
    xs: circle('xs'),
    sm: circle('sm'),
    md: circle('md'),
    lg: circle('lg'),
};

/** The avatar and the name it belongs to, on one line. */
export const personLabelStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
};

/** Long names truncate rather than wrapping a table cell into two lines. */
export const personNameStyles: SxProps<Theme> = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};
